import type { CSSProperties } from 'react';
import type { OccurrenceResponse } from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { buildMonthGrid, toDateKey } from './calendar-dates';

/** Jan 1 2023 was a Sunday — a fixed reference week, independent of the
 *  visible month, used only to render locale-correct weekday abbreviations. */
const WEEKDAY_REFERENCE = Date.UTC(2023, 0, 1);

function weekdayLabels(locale: string): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    formatDate(new Date(WEEKDAY_REFERENCE + i * 86_400_000), locale, { weekday: 'short' }, 'UTC'),
  );
}

export function CalendarGrid({
  viewDate,
  occurrences,
  locale,
  onSelectOccurrence,
}: {
  readonly viewDate: Date;
  readonly occurrences: readonly OccurrenceResponse[];
  readonly locale: string;
  readonly onSelectOccurrence: (occurrence: OccurrenceResponse) => void;
}) {
  const days = buildMonthGrid(viewDate);
  const todayKey = toDateKey(new Date(Date.now()));
  const currentMonth = viewDate.getUTCMonth();

  const byDate = new Map<string, OccurrenceResponse[]>();
  for (const occurrence of occurrences) {
    const key = toDateKey(occurrence.startAt);
    const bucket = byDate.get(key);
    if (bucket) bucket.push(occurrence);
    else byDate.set(key, [occurrence]);
  }
  for (const bucket of byDate.values()) {
    bucket.sort((a, b) => a.startAt.localeCompare(b.startAt));
  }

  return (
    <div className="calendar-grid" role="grid">
      <div className="calendar-grid-weekdays" role="row">
        {weekdayLabels(locale).map((label, i) => (
          <span key={i} className="calendar-grid-weekday" role="columnheader">
            {label}
          </span>
        ))}
      </div>

      <div className="calendar-grid-body">
        {days.map((day) => {
          const key = toDateKey(day);
          const dayOccurrences = byDate.get(key) ?? [];
          const inMonth = day.getUTCMonth() === currentMonth;

          return (
            <div
              key={key}
              className="calendar-grid-cell"
              role="gridcell"
              data-outside-month={!inMonth || undefined}
              data-today={key === todayKey || undefined}
            >
              <span className="calendar-grid-day-number">{day.getUTCDate()}</span>
              <div className="calendar-grid-day-occurrences">
                {dayOccurrences.map((occurrence) => (
                  <button
                    key={occurrence.id}
                    type="button"
                    className="calendar-chip"
                    style={{ '--chip-color': occurrence.calendarColor } as CSSProperties}
                    onClick={() => onSelectOccurrence(occurrence)}
                    title={occurrence.title}
                  >
                    {!occurrence.isAllDay && (
                      <span className="calendar-chip-time">
                        {formatDate(
                          occurrence.startAt,
                          locale,
                          { hour: 'numeric', minute: '2-digit' },
                          'UTC',
                        )}
                      </span>
                    )}
                    <span className="calendar-chip-title">{occurrence.title}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
